import type { ComposeNotice, SseEvent } from "@redchain/contracts";
import type { OfflineResult } from "../main/SessionClient";
import { linkResultText, t } from "../strings";

export type SyncPhase = "idle" | "uploading" | "verifying" | "syncing" | "synced" | "rejected";

export interface Banner {
  id: string;
  kind: "ownership_changed" | "released" | "vault_funded" | "taint" | "items_waiting" | "overflow" | "info" | "offline_discarded";
  /** Asset whose display name is resolved asynchronously, if any. */
  asset?: string;
  name?: string;
  direction?: "in" | "out";
  lamports?: string;
  text?: string;
}

/** §7.6 overlay states, driven by SSE and the upload queue. */
export interface OverlayState {
  phase: SyncPhase;
  progress: { done: number; total: number } | null;
  signature: string | null;
  lastSyncedAt: number | null;
  rejectedReason: string | null;
  /** "Auto-saved · …" for two seconds after an auto-save. */
  autosave: { text: string; until: number } | null;
  /** Mints and rare-item records the planner is holding back (§16.1). */
  waiting: VaultWaiting | null;
  banners: Banner[];
  superseded: boolean;
  syncPendingWaiting: boolean;
  pendingCheckpoints: number;
  online: boolean;
  offlineSession: boolean;
}

export const initialOverlayState: OverlayState = {
  phase: "idle",
  progress: null,
  signature: null,
  lastSyncedAt: null,
  rejectedReason: null,
  autosave: null,
  waiting: null,
  banners: [],
  superseded: false,
  syncPendingWaiting: false,
  pendingCheckpoints: 0,
  online: true,
  offlineSession: false,
};

export type OverlayAction =
  | { type: "sse"; event: SseEvent }
  | { type: "queue"; pendingCheckpoints: number; online: boolean }
  | { type: "checkpointUploaded" }
  | { type: "autosave"; text: string; now: number }
  | { type: "autosaveExpired"; now: number }
  | { type: "settle" }
  | { type: "superseded" }
  | { type: "syncPending"; waiting: boolean }
  | { type: "notices"; notices: ComposeNotice[] }
  | { type: "dismiss"; id: string }
  | { type: "init"; lastSyncedAt: number | null; offline: boolean; vault?: VaultWaiting }
  | { type: "vault"; vault: VaultWaiting }
  | { type: "offline"; result: OfflineResult };

/** Mint deposits are paid in ₽ from the rent pool; only the rare-item bag still needs vault SOL. */
export interface VaultWaiting {
  pendingMints: number;
  pendingItemBag: boolean;
  topUpNeeded: string;
  mintRcdNeeded: string;
  rentPoolShort: boolean;
}

const AUTOSAVE_MS = 2_000;
let bannerSeq = 0;
const nextId = () => `b${++bannerSeq}`;

function waitingFrom(v: VaultWaiting): OverlayState["waiting"] {
  const mintsBlocked = v.pendingMints > 0 && (v.rentPoolShort || BigInt(v.mintRcdNeeded) > 0n);
  const itemsBlocked = v.pendingItemBag && BigInt(v.topUpNeeded) > 0n;
  return mintsBlocked || itemsBlocked ? v : null;
}

export function overlayReducer(s: OverlayState, a: OverlayAction): OverlayState {
  switch (a.type) {
    case "init":
      return { ...s, lastSyncedAt: a.lastSyncedAt, offlineSession: a.offline, waiting: a.vault ? waitingFrom(a.vault) : s.waiting };
    case "vault":
      return { ...s, waiting: waitingFrom(a.vault) };
    case "offline": {
      const r = a.result;
      if (r.kind === "adopted") {
        const banners = [...s.banners, { id: nextId(), kind: "info" as const, text: t.offlineAdopted(r.live) }];
        return r.live ? { ...s, offlineSession: false, online: true, banners } : { ...s, banners };
      }
      const banners = [...s.banners, { id: nextId(), kind: "offline_discarded" as const, text: t.offlineDiscarded(r.reason, r.live) }];
      // Nothing from this session will upload any more, so stop promising it.
      return r.live ? { ...s, offlineSession: false, phase: s.phase === "uploading" ? "idle" : s.phase, pendingCheckpoints: 0, banners } : { ...s, banners };
    }
    case "queue": {
      const next = { ...s, pendingCheckpoints: a.pendingCheckpoints, online: a.online };
      if (a.pendingCheckpoints > 0 && (s.phase === "idle" || s.phase === "synced")) next.phase = "uploading";
      return next;
    }
    case "checkpointUploaded":
      return s.phase === "uploading" || s.phase === "idle" || s.phase === "synced" ? { ...s, phase: s.pendingCheckpoints > 0 ? "uploading" : "verifying" } : s;
    case "autosave":
      return { ...s, autosave: { text: a.text, until: a.now + AUTOSAVE_MS } };
    case "autosaveExpired":
      return s.autosave && s.autosave.until <= a.now ? { ...s, autosave: null } : s;
    case "settle":
      return s.phase === "synced" ? { ...s, phase: "idle" } : s;
    case "superseded":
      return { ...s, superseded: true };
    case "syncPending":
      return { ...s, syncPendingWaiting: a.waiting };
    case "dismiss":
      return { ...s, banners: s.banners.filter((b) => b.id !== a.id) };
    case "notices": {
      const banners = [...s.banners];
      for (const n of a.notices) {
        if (n.kind === "ownership_changed") banners.push({ id: nextId(), kind: "ownership_changed", asset: n.asset, name: n.nickname, direction: n.direction });
        else if (n.kind === "overflow") banners.push({ id: nextId(), kind: "overflow", text: `${n.count} Pokémon didn't fit in your party or box and are waiting on chain.` });
        else if (n.kind === "items_waiting") banners.push({ id: nextId(), kind: "items_waiting", text: `${n.amount} rare item(s) are waiting for space in your bag or PC.` });
        else if (n.kind === "glitch_money_dropped") banners.push({ id: nextId(), kind: "taint", text: "Glitch money from your last session was removed at load." });
        else if (n.kind === "purse_capped") banners.push({ id: nextId(), kind: "info", text: "Your purse is capped at ₽999,999; the rest stays as in-game balance." });
      }
      return { ...s, banners };
    }
    case "sse":
      return applySse(s, a.event);
  }
}

function applySse(s: OverlayState, ev: SseEvent): OverlayState {
  switch (ev.type) {
    case "checkpoint.received":
    case "checkpoint.verified":
      return s.phase === "syncing" ? s : { ...s, phase: "verifying", rejectedReason: null };
    case "checkpoint.rejected":
      return { ...s, phase: "rejected", rejectedReason: ev.reason, progress: null };
    case "chain.progress":
      return { ...s, phase: "syncing", progress: { done: ev.done, total: ev.total } };
    case "chain.committed":
      return { ...s, phase: "synced", signature: ev.signature, lastSyncedAt: Date.now(), progress: null, rejectedReason: null };
    case "session.superseded":
      return { ...s, superseded: true };
    case "ownership.changed":
      return { ...s, banners: [...s.banners, { id: nextId(), kind: "ownership_changed", asset: ev.asset, direction: ev.direction }] };
    case "mon.refunded":
      return { ...s, banners: [...s.banners, { id: nextId(), kind: "released", asset: ev.asset, lamports: ev.lamports }] };
    case "vault.changed":
      return { ...s, waiting: waitingFrom(ev) };
    case "vault.funded":
      return s.waiting?.pendingItemBag ? { ...s, banners: [...s.banners, { id: nextId(), kind: "vault_funded" }] } : s;
    case "taint.detected":
      return s.banners.some((b) => b.kind === "taint") ? s : { ...s, banners: [...s.banners, { id: nextId(), kind: "taint" }] };
    case "sync.pending_cleared":
      return { ...s, syncPendingWaiting: false };
    case "link.result":
      return { ...s, banners: [...s.banners, { id: nextId(), kind: "info", text: linkResultText(ev) }] };
    default:
      return s;
  }
}
